import { IGrid } from "@dhx/ts-grid";
import { DataPage, DataStore, Store, IFormula } from "@xbs/muon";
import { Actions, IBufferStruct, ICell, IExecuteConfig, ISpreadsheet, SpreadsheetEvents, ISpan } from "./../types";
import {
	getCellIndex,
	getCellInfo,
	getCellNameByIndex,
	getCellsArray,
	getRangeIndexes,
	isRangeId,
	getCellIds,
	doesContainUrl,
	isCellSpaned,
	checkSpanByNames,
	showAlert,
} from "./main";
import { getCellsDiff } from "./selection";
import en from "../locales/en";

declare const dhx: any;

export class BufferManager {
	private _buffer: IBufferStruct;
	private _spreadsheet: ISpreadsheet;
	private _grid: IGrid;
	private _callAction: (config: IExecuteConfig | IExecuteConfig[]) => void;
	private _activePage: DataPage;
	private _math: Store;
	private _dataStore: DataStore;
	private _prevCopyPage: DataPage;

	constructor(
		spreadsheet: ISpreadsheet,
		grid: IGrid,
		callAction: (config: IExecuteConfig) => void,
		page: DataPage,
		dataStore: DataStore
	) {
		this._spreadsheet = spreadsheet;
		this._grid = grid;
		this._activePage = page;
		this._math = dataStore.getStore();
		this._dataStore = dataStore;
		// [dirty]
		this._callAction = callAction.bind(spreadsheet);

		this._buffer = {
			value: "",
			math: [],
			meta: [],
			styles: {},
			cell: "",
			cells: "",
			operation: "",
			inserted: false,
			formats: [],
			html: "",
			spans: [],
		};
		this._setHandlers();
	}

	store(operation: IBufferStruct["operation"]) {
		const focus = this._spreadsheet.selection.getFocusedCell();
		const cellInfo = getCellInfo(focus, this._activePage, this._grid);
		let cell = this._spreadsheet.selection.getSelectedCell();

		if (this._prevCopyPage) {
			this._prevCopyPage.setMeta({ inserted: true });
		}

		if (cellInfo.edited) {
			return;
		}

		const range = isRangeId(cell);
		let arrOfCells = [cell];

		if (range && cell.includes(",")) {
			arrOfCells = cell.split(",");
			const lastCopyCell = arrOfCells[arrOfCells.length - 1];

			if (!cell.includes(":")) {
				cell = lastCopyCell;
				arrOfCells = [cell];
			} else if (!lastCopyCell.includes(":")) {
				cell = lastCopyCell;
				arrOfCells = [cell];
			} else {
				let firstRange: string;
				let prevItem: string;
				let resArr: string[];
				let firstRangeCoords: {
					start: ICell;
					end: ICell;
				};
				let dir: string;
				for (let i = 0; i < arrOfCells.length; i++) {
					const item = arrOfCells[i];
					if (item.includes(":") && !firstRange) {
						firstRange = item;
						firstRangeCoords = {
							start: getCellIds(firstRange.split(":")[0], this._grid),
							end: getCellIds(firstRange.split(":")[1], this._grid),
						};
					}

					if (i) {
						if (!item.includes(":") && !prevItem.includes(":")) {
							const checkFirstRange =
								firstRangeCoords.start.row === firstRangeCoords.end.row ||
								firstRangeCoords.start.col === firstRangeCoords.end.col;

							if (checkFirstRange) {
								const dir =
									firstRangeCoords.start.row === firstRangeCoords.end.row ? "row" : "col";
								const cellId = getCellIds(item, this._grid)[dir];
								if (firstRangeCoords.start[dir] === cellId[dir]) {
									resArr.push(item);
								}
							} else {
								cell = lastCopyCell;
								i = arrOfCells.length;
								resArr = [cell];
							}
						}

						if (item.includes(":") && item !== firstRange) {
							const firstCellId = getCellIds(item.split(":")[0], this._grid);
							const lastCellId = getCellIds(item.split(":")[1], this._grid);
							const firstCellCheck =
								firstCellId.row === firstRangeCoords.start.row ||
								firstCellId.row === firstRangeCoords.end.row ||
								firstCellId.col === firstRangeCoords.start.col ||
								firstCellId.col === firstRangeCoords.end.col;

							const lastCellCheck =
								lastCellId.row === firstRangeCoords.start.row ||
								lastCellId.row === firstRangeCoords.end.row ||
								lastCellId.col === firstRangeCoords.start.col ||
								lastCellId.col === firstRangeCoords.end.col;

							const isSameRange = firstCellCheck && lastCellCheck;
							const currentDir = firstCellId.row === firstRangeCoords.start.row ? "col" : "row";

							if (!dir) {
								dir = currentDir;
							}

							if (isSameRange && dir === currentDir) {
								resArr.push(item);
							} else {
								cell = lastCopyCell;
								i = arrOfCells.length;
								resArr = [cell];
							}
						}
					} else {
						resArr = [item];
					}

					prevItem = item;
				}

				arrOfCells = resArr;
				if (arrOfCells.length > 1) {
					cell = arrOfCells.join();
				}
			}
		}
		if (isRangeId(cell) && !cell.includes(":")) {
			this._buffer.cell = null;
			return;
		}
		const res = [];
		let isColDir;
		if (isRangeId(arrOfCells[0])) {
			const firstCellRow = getCellIds(arrOfCells[0].split(":")[0], this._grid).row;
			const lastCellRow = getCellIds(arrOfCells[0].split(":")[1], this._grid).row;
			const nextCellRow = arrOfCells[1] && getCellIds(arrOfCells[1].split(":")[0], this._grid).row;
			isColDir = nextCellRow && (nextCellRow === firstCellRow || nextCellRow === lastCellRow);
		}
		let currentRow = 0;
		for (let i = 0; i < arrOfCells.length; i++) {
			let index = 0;
			let prevCell;
			currentRow = isColDir ? 0 : currentRow;
			const range = arrOfCells[i];

			this._spreadsheet.eachCell((c, val) => {
				if (!val) {
					val = " ";
				}
				if (!index) {
					res[currentRow] ? (res[currentRow] += "\t" + val) : res.push(`${val}`);
				} else {
					const prevCellId = getCellIds(prevCell, this._grid);
					const cellId = getCellIds(c, this._grid);
					if (prevCellId.row === cellId.row) {
						res[currentRow] ? (res[currentRow] += "\t" + val) : res.push(`${val}`);
					} else {
						currentRow++;
						res[currentRow] ? (res[currentRow] += "\t" + val) : res.push(`${val}`);
					}
				}
				prevCell = c;
				index++;
			}, range);
			currentRow++;
		}
		this._buffer.value = res.join("\n");
		this._buffer.styles = this._spreadsheet.getStyle(cell);
		const { merged } = this._activePage.getMeta();
		const spans: any[] = [];
		if (isRangeId(cell)) {
			const math: IFormula[] = [];
			const cellArr = getCellsArray(cell, this._grid);
			this._spreadsheet.eachCell(c => {
				math.push((this._spreadsheet as any)._getMath(c));
				const span = isCellSpaned(c, merged, this._grid);
				if (span && !spans.includes(span)) {
					const { from, to } = span;
					const firstCell = getCellNameByIndex(from.row, from.column + 1, this._grid);
					const lastCell = getCellNameByIndex(to.row, to.column + 1, this._grid);
					if (cellArr.includes(firstCell) && cellArr.includes(lastCell) && firstCell === c) {
						spans.push(span);
					}
				} else {
					spans.push(null);
				}
			}, cell);
			this._buffer.math = math;
		} else {
			const math = (this._spreadsheet as any)._getMath(cell);
			this._buffer.math = [math];
			const span = isCellSpaned(cell, merged, this._grid);
			if (span && !spans.includes(span)) {
				spans.push(span);
			}
		}

		this._buffer.spans = spans;
		this._buffer.cells = cell;
		this._buffer.meta = [];
		this._spreadsheet.eachCell((c) => {
			const { row, col } = getCellIds(c, this._grid);
			this._buffer.meta.push(this._activePage.getCell(row, col) || {});
		}, cell);
		this._buffer.formats = this._spreadsheet.getFormat(cell);
		this._buffer.cell = focus;
		this._buffer.operation = operation;
		this._activePage.setMeta({ inserted: false });
		this._prevCopyPage = this._activePage;
		this._grid.paint();
	}

	paste() {
		if (!this._buffer.cell) {
			return;
		}

		this._activePage.setMeta({ inserted: true });
		const cell = this._buffer.cell;
		const format = this._buffer.formats;
		let newCell = cell;

		let isRangeCells = false;

		if (isRangeId(this._buffer.cells)) {
			isRangeCells = true;

			const index = getRangeIndexes(this._buffer.cells, this._grid);
			const to = getCellIndex(cell, this._grid);
			const diff = {
				row: index.end.row - index.start.row,
				col: index.end.col - index.start.col,
			};

			const lastCell = getCellNameByIndex(to.row + diff.row, to.col + diff.col, this._grid);

			if (isRangeId(cell)) {
				const bufferCellsLength = getCellsArray(this._buffer.cells, this._grid).length;
				const targetCellsLength = getCellsArray(cell, this._grid).length;

				if (bufferCellsLength > targetCellsLength) {
					newCell = `${cell.split(":")[0]}:${lastCell}`;
				}
			} else {
				newCell += `:${lastCell}`;
			}
		}

		const isRangeTarget = isRangeId(newCell);

		const styles = Array.isArray(this._buffer.styles)
			? this._buffer.styles.map(obj => dhx.css.add(obj))
			: dhx.css.add(this._buffer.styles);
		const actionsGroup: IExecuteConfig[] = [];
		if (this._buffer.operation === "cut") {
			actionsGroup.push(
				{
					cell: this._buffer.cells,
					action: Actions.setCellFormat,
					groupAction: isRangeTarget ? Actions.groupAction : null,
					val: "",
					page: this._activePage,
					dataStore: this._dataStore,
				},
				{
					cell: this._buffer.cells,
					action: Actions.setCellValue,
					val: "",
					page: this._activePage,
					groupAction: isRangeCells ? Actions.groupAction : null,
					dataStore: this._dataStore,
				},
				{
					cell: this._buffer.cells,
					action: Actions.setCellStyle,
					val: "",
					page: this._activePage,
					groupAction: isRangeCells ? Actions.groupAction : null,
					dataStore: this._dataStore,
				},
				{
					cell: this._buffer.cells,
					action: Actions.setValidation,
					val: null,
					page: this._activePage,
					groupAction: isRangeCells ? Actions.groupAction : null,
					dataStore: this._dataStore,
				},
				{
					cell: this._buffer.cells,
					action: Actions.insertLink,
					val: null,
					page: this._activePage,
					groupAction: isRangeCells ? Actions.groupAction : null,
					dataStore: this._dataStore,
				}
			);
			// remove spans
			this._buffer.spans.forEach((span: ISpan) => {
				if (!span) return;
				const {from} = span;
				const firstCell = getCellNameByIndex(from.row, from.column + 1, this._grid);
				actionsGroup.push({
					action: Actions.unmerge,
					groupAction: Actions.groupAction,
					dataStore: this._dataStore,
					cell: firstCell,
					page: this._activePage,
				});
			});

			this._buffer.operation = "copy";
		}

		// get values from buffer
		let values = (isRangeCells ? [].concat(this._buffer.value) : this._buffer.value) as string[];
		const maths = this._buffer.math;
		const options = this._buffer.meta.map(i => i.editor?.options || null);
		const link = this._buffer.meta.map(i => i.link || null);
		const valuesLength = values.length;

		// get info about start range
		let from: { row: number; col: number };
		let dx: number;
		// let dy: number;

		if (isRangeCells) {
			const indexes = getRangeIndexes(this._buffer.cells, this._grid);
			from = indexes.start;
			dx = indexes.end.col - indexes.start.col + 1;
			// dy = indexes.end.row - indexes.start.row + 1;
		} else {
			from = getCellIndex(this._buffer.cells, this._grid);
			dx = 1;
			// dy = 1;
		}

		let i = 0;
		const { merged } = this._activePage.getMeta();
		this._spreadsheet.eachCell(aCell => {
			const iSource = i % valuesLength;
			let value = values[iSource];
			const math = maths[iSource];
			if (math) {
				const ix = iSource % dx;
				const iy = (iSource - ix) / dx;
				const to = getCellIndex(aCell, this._grid);
				value =
					"=" +
					this._math.transposeMath(math.source, to.row - from.row - iy, to.col - from.col - ix);
			}
			values[i] = value;

			i++;
		}, newCell);

		values = values.map((v,i)=> {
			if (doesContainUrl(v)) {
				link[i] = {href: v}
				return ""
			}
			return v;
		})

		actionsGroup.push(
			{
				cell: newCell,
				action: Actions.setCellFormat,
				groupAction: isRangeTarget ? Actions.groupAction : null,
				val: format,
				page: this._activePage,
				dataStore: this._dataStore,
			},
			{
				cell: newCell,
				action: Actions.setCellStyle,
				val: styles,
				page: this._activePage,
				groupAction: isRangeTarget ? Actions.groupAction : null,
				dataStore: this._dataStore,
			},
			{
				cell: newCell,
				action: Actions.setCellValue,
				val: values,
				page: this._activePage,
				groupAction: Actions.groupAction,
				dataStore: this._dataStore,
			},
			{
				cell: newCell,
				action: Actions.setValidation,
				val: options,
				page: this._activePage,
				groupAction: Actions.groupAction,
				dataStore: this._dataStore,
			},
			{
				cell: newCell,
				action: Actions.insertLink,
				val: link,
				page: this._activePage,
				groupAction: Actions.groupAction,
				dataStore: this._dataStore,
			}
		);
		let err = false;
		
		const grid = (this._spreadsheet as any)._grid as IGrid;
		if (this._buffer.cells) {
			let index = 0;
			const focusedArr = getCellsArray(this._buffer.cells, grid);
			const { leftSplit, topSplit } = this._spreadsheet.config;
			this._spreadsheet.eachCell(c => {
				const span = this._buffer.spans[index];
				if (span) {
					const { x, y } = getCellsDiff(focusedArr[index], c, grid);
					const firstCell = getCellNameByIndex(span.from.row + y, span.from.column + x + 1, grid);
					const lastCell = getCellNameByIndex(span.to.row + y, span.to.column + x + 1, grid);
					const spanedCells = `${firstCell}:${lastCell}`;
					if (!checkSpanByNames(spanedCells, leftSplit, topSplit, grid)) {
						showAlert(en.spanPasteError)
						err = true;
						return;
					}
					if (isCellSpaned(c, merged, this._grid) || !(this._spreadsheet as any)._spanControl?.checkBeforeMerge(spanedCells)) {
						return;
					}
					actionsGroup.push({
						action: Actions.merge,
						groupAction: Actions.merge,
						dataStore: this._dataStore,
						cell: spanedCells,
						page: this._activePage,
						$autofill: true,
					});
				}
				index = index === focusedArr.length - 1 ? 0 : ++index;
			}, newCell);
		}
		if (err) return;

		this._callAction(actionsGroup);
		const {end} = getCellIds(newCell, grid);
		const gridRows = grid.data.getLength();
		const gridCols = grid.config.columns.length;
		if (gridRows < end.row + 1 || gridCols <= end.col + 1) {
			(this._spreadsheet as any)._updateGridSizes();
		}
		this._spreadsheet.selection.setSelectedCell(newCell);
	}

	getStruct(): IBufferStruct {
		return this._buffer;
	}

	private _setHandlers() {
		this._spreadsheet.events.on(SpreadsheetEvents.afterSheetChange, () => {
			this._activePage = (this._spreadsheet as any)._activePage;
		});
	}
}
